// React hook over `connectSessionStream`.
//
// **Layering**: fn-1.28 landed the pure `connectSessionStream(opts)`
// primitive in `streams/session-stream.ts` — no React, no reducer, just
// an EventSource wrapper with listener fan-out. fn-1.29 adds the
// React layer here: subscribe to the stream, reduce frames into a
// `ChatMessage[]` projection the run-detail page can render directly,
// and tear down on unmount.
//
// **Why two files**: the primitive is testable without React, the
// hook is testable with a fake `connectSessionStream`. Splitting them
// also keeps the "spec says no httpSubscriptionLink" contract clean
// — `streams/` is the EventSource path, period.
//
// **What we PORT from v0** (`~/sb-ai-platform/packages/web/src/hooks/use-session-stream.ts`):
//   - The reducer SHAPE — user/agent message accumulation, tool_use,
//     tool_result, tool_confirmation, requires_action pending card.
//   - The `pendingApproval` derivation from
//     `session.status_idle{stop_reason: requires_action, event_ids}`.
// **What we ADAPT**:
//   - The v0 wire shape lifted MA events through a Smokeball
//     `streamSession` helper. Here we receive raw BFF re-fan SSE
//     frames whose `data` is the MA event envelope. We narrow the
//     payload defensively (the SPA never imports MA wire types).
//   - The v0 hook owned its EventSource + reconnect logic. Here that
//     lives in `connectSessionStream`; the hook only owns reduce +
//     React state.
// **What we DROP**:
//   - The v0 projection channels (requires-action, outcome) — those
//     are out-of-band SSE frames that the control plane doesn't emit
//     at v1. Acceptance covers the canonical event log only.
//   - RAF coalescing for high-frequency deltas. Not yet wired in v1;
//     the agent's text streaming uses `agent.message` (atomic) not
//     `agent.message_delta`. If a future task surfaces deltas we
//     re-introduce the RAF batcher here.

import { useEffect, useMemo, useReducer, useRef } from 'react';

import {
  type SessionStreamFrame,
  type SessionStreamHandle,
  buildSessionStreamUrl,
  connectSessionStream,
} from './session-stream.js';

/**
 * The MA event envelope as it arrives on the wire from the BFF re-fan.
 * The control plane re-emits the event log frame-for-frame from MA;
 * the BFF forwards verbatim. We type the shape narrowly + treat every
 * payload as `unknown` until proven otherwise.
 */
export interface SessionEventOnWire {
  /** Opaque event id. */
  readonly id: string;
  /** MA event kind — `agent.message`, `agent.tool_use`, etc. */
  readonly type: string;
  /** Verbatim MA payload. */
  readonly data: Record<string, unknown>;
  /** Optional Smokeball annotation block (decision actor for requires_action). */
  readonly smokeball?: Record<string, unknown>;
  /**
   * ISO 8601 timestamp from the MA envelope. Optional because some
   * legacy event paths may omit it; rendered when present so the
   * operator can see when each event landed.
   */
  readonly createdAt?: string;
}

/**
 * The chat message projection. Each turn collapses to one of these.
 * Tool-use / tool-result are rendered as their own rows so the user
 * can see the agent's tool plan + the result.
 */
export type ChatMessage =
  | {
      readonly kind: 'user';
      readonly id: string;
      readonly content: string;
      /**
       * fn-2.2 — true when this user.message is the auto-injected
       * `<system-bootstrap>` turn the CP fires on session create. The
       * transcript view hides bootstrap rows by default; a "Show
       * bootstrap" toggle in the header unhides them for debug.
       */
      readonly isBootstrap?: boolean;
    }
  | {
      readonly kind: 'assistant';
      readonly id: string;
      readonly content: string;
      /** Token usage attached when the wire carries `usage.{input,output}_tokens`. */
      readonly usage?: { readonly input?: number; readonly output?: number };
      /**
       * fn-2.2 — true when this agent.message is the agent's ack of
       * the auto-injected bootstrap ("I have processed the context …").
       * Same default-hidden treatment as `isBootstrap`.
       */
      readonly isBootstrapAck?: boolean;
      /**
       * fn-2.x — Claude's extended-thinking output rides on the
       * same agent.message event as content blocks with
       * `type: 'thinking'` (alongside the `type: 'text'` answer).
       * Surfaced here so the renderer can show a collapsed "💭 Show
       * thinking" disclosure above the answer text.
       */
      readonly thinking?: string;
    }
  | {
      readonly kind: 'tool_use';
      readonly id: string;
      readonly toolName: string;
      readonly input: Record<string, unknown>;
      readonly toolUseId: string;
      /**
       * HITL decision attached to this tool_use, once a matching
       * `user.tool_confirmation` event arrives. Inlined on the row
       * because the confirmation event is timestamped at the moment
       * the operator clicked Approve — which is conceptually BEFORE
       * the tool result but lands AFTER it in the MA event log.
       * Rendering as a sibling row would put the decision visually
       * after results that came after it; this collapses the gap.
       */
      readonly decision?: 'allow' | 'deny';
      readonly decidedAt?: string;
      readonly decidedBy?: string;
      readonly denyMessage?: string;
      /**
       * fn-2.x — bundled tool result. When the matching
       * `agent.tool_result` event arrives, the reducer attaches it
       * here rather than pushing a separate row, so the transcript
       * shows one card per call (input + output + decision
       * together). The legacy `tool_result` ChatMessage kind still
       * exists for results whose originating tool_use we never saw
       * (race/replay edge case) — those fall back to a standalone
       * row so the result isn't lost.
       */
      readonly result?: {
        readonly id: string;
        readonly output: string;
        readonly isError?: boolean;
      };
    }
  | {
      readonly kind: 'tool_result';
      readonly id: string;
      readonly toolUseId: string;
      readonly output: string;
      readonly isError?: boolean;
    }
  | {
      readonly kind: 'tool_confirmation';
      readonly id: string;
      readonly toolUseId: string;
      readonly decision: 'allow' | 'deny';
      readonly decidedAt?: string;
      readonly decidedBy?: string;
      readonly denyMessage?: string;
    };

/**
 * Pending HITL approval surfaced from a `session.status_idle{requires_action}`
 * frame. The streaming-run view renders the approve / reject card based on
 * this; cleared when a matching `user.tool_confirmation` arrives.
 *
 * **Lookup key**: the `event_ids` field on the idle frame references
 * the originating `agent.tool_use` event(s). v1 always emits a single
 * id; if multiple show up (future batching) we honor the first
 * resolvable.
 */
export interface PendingApproval {
  /** The originating `agent.tool_use` event id (used as the approval correlator on the BFF side). */
  readonly eventId: string;
  /** Anthropic tool-use correlator from the agent.tool_use payload. */
  readonly toolUseId: string;
  readonly toolName: string;
  readonly input: Record<string, unknown>;
}

export interface StreamState {
  readonly messages: readonly ChatMessage[];
  /** Most recent session status from `session.status_*` events; `unknown` until first frame. */
  readonly status: string;
  /** Connection state of the underlying EventSource. */
  readonly connection: 'connecting' | 'open' | 'closed';
  /** Currently unresolved HITL gate, or null. */
  readonly pendingApproval: PendingApproval | null;
  /**
   * Last error from the session (mapped from `session.error` event).
   * Display-only.
   */
  readonly lastError: { readonly type: string; readonly message: string } | null;
  /** Raw event log (kept for the requires_action lookup). */
  readonly events: readonly SessionEventOnWire[];
  /** Last seen event id — exposed for tests + diagnostic UI. */
  readonly lastEventId: string;
  /**
   * Running token totals aggregated from every `span.model_request_end`
   * event observed on this stream. `cost_used_tokens` on the session
   * row is updated server-side (or will be once the webhook handler
   * lands the column update); until then, this is the live source of
   * truth for the operator UI.
   */
  readonly tokens: {
    readonly input: number;
    readonly output: number;
    readonly cacheRead: number;
    readonly cacheCreate: number;
  };
  /**
   * fn-2.2 — transient flag: set when the bootstrap user.message has
   * been observed and we are waiting for the agent's ack reply. The
   * first subsequent `agent.message` claims the ack-tag and clears
   * this. A real (non-bootstrap) user.message arriving first also
   * clears it — the bootstrap had no ack and we move on.
   */
  readonly expectBootstrapAck: boolean;
}

type Action =
  | { readonly type: 'event'; readonly event: SessionEventOnWire; readonly lastEventId: string }
  | { readonly type: 'connection'; readonly state: StreamState['connection'] }
  | { readonly type: 'seed'; readonly events: readonly SessionEventOnWire[] }
  | { readonly type: 'reset' };

function initialState(): StreamState {
  return {
    messages: [],
    status: 'unknown',
    connection: 'closed',
    pendingApproval: null,
    lastError: null,
    events: [],
    lastEventId: '',
    tokens: { input: 0, output: 0, cacheRead: 0, cacheCreate: 0 },
    expectBootstrapAck: false,
  };
}

/**
 * fn-2.2 — the CP wraps every auto-injected context/mount message in a
 * `<system-bootstrap>...</system-bootstrap>` sentinel. We detect it
 * client-side rather than carrying a server-stamped pointer because:
 *   (a) the wire shape is invariant — we own both ends, and
 *   (b) it avoids a Provider-interface change to fetch the bootstrap
 *       message id (MA's POST /events returns 204 with no body).
 * If the sentinel ever feels brittle, swap this for a stamped
 * `sessions.metadata.bootstrap_message_id` and read it via runs.byId.
 */
function isBootstrapContent(text: string): boolean {
  // Tolerate a leading BOM / whitespace but require the tag at start.
  return /^\s*<system-bootstrap>/.test(text);
}

function extractText(payload: unknown): string {
  if (payload === null || typeof payload !== 'object') return '';
  const content = (payload as { content?: unknown }).content;
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    let acc = '';
    for (const block of content) {
      if (
        block !== null &&
        typeof block === 'object' &&
        (block as { type?: unknown }).type === 'text' &&
        typeof (block as { text?: unknown }).text === 'string'
      ) {
        acc += (block as { text: string }).text;
      }
    }
    return acc;
  }
  return '';
}

/**
 * fn-2.x — Claude's extended-thinking blocks ride on `agent.message`
 * alongside `type: 'text'` blocks (`content: [{type: 'thinking',
 * thinking: '...', signature: '...'}, {type: 'text', text: '...'}]`).
 * Returns concatenated thinking text, or '' when none present.
 *
 * **Field name**: the wire uses `thinking` for the content; some
 * older MA event variants used `text` for the same role — we accept
 * both for forward/backward compatibility.
 */
function extractThinking(payload: unknown): string {
  if (payload === null || typeof payload !== 'object') return '';
  const content = (payload as { content?: unknown }).content;
  if (!Array.isArray(content)) return '';
  let acc = '';
  for (const block of content) {
    if (
      block === null ||
      typeof block !== 'object' ||
      (block as { type?: unknown }).type !== 'thinking'
    ) {
      continue;
    }
    const t = (block as { thinking?: unknown; text?: unknown }).thinking;
    if (typeof t === 'string') {
      acc += t;
      continue;
    }
    const alt = (block as { text?: unknown }).text;
    if (typeof alt === 'string') acc += alt;
  }
  return acc;
}

/**
 * Derive a `PendingApproval` from a `session.status_idle{requires_action}`
 * payload + the in-state event log. Returns null when no `event_ids`
 * resolve to an `agent.tool_use` (defensive — should not happen on a
 * well-formed stream).
 */
function derivePending(
  events: readonly SessionEventOnWire[],
  stopReason: unknown,
): PendingApproval | null {
  if (stopReason === null || typeof stopReason !== 'object') return null;
  const r = stopReason as { event_ids?: unknown };
  if (!Array.isArray(r.event_ids)) return null;
  for (const rawId of r.event_ids) {
    if (typeof rawId !== 'string') continue;
    const tu = events.find(
      (e) => e.id === rawId && (e.type === 'agent.tool_use' || e.type === 'agent.custom_tool_use'),
    );
    if (!tu) continue;
    const p = tu.data as {
      tool_use_id?: unknown;
      custom_tool_use_id?: unknown;
      name?: unknown;
      input?: unknown;
    } | null;
    if (p === null || typeof p !== 'object' || typeof p.name !== 'string') {
      continue;
    }
    // Anthropic-direct doesn't include `tool_use_id` on the
    // agent.tool_use event payload. Same fallback as the message
    // renderer + the platform's webhook handler: use the event id
    // as the synthetic tool_use_id so downstream matches (the
    // tool_use ↔ tool_result pairing) still work. Custom tools
    // (ask_user_question) carry `custom_tool_use_id` instead.
    const toolUseId =
      typeof p.tool_use_id === 'string' && p.tool_use_id.length > 0
        ? p.tool_use_id
        : typeof p.custom_tool_use_id === 'string' && p.custom_tool_use_id.length > 0
          ? p.custom_tool_use_id
          : tu.id;
    return {
      eventId: tu.id,
      toolUseId,
      toolName: p.name,
      input:
        p.input !== undefined &&
        p.input !== null &&
        typeof p.input === 'object' &&
        !Array.isArray(p.input)
          ? (p.input as Record<string, unknown>)
          : {},
    };
  }
  return null;
}

export function reduceStream(state: StreamState, action: Action): StreamState {
  if (action.type === 'reset') return initialState();
  if (action.type === 'connection') return { ...state, connection: action.state };
  if (action.type === 'seed') {
    // Seed events come from runs.events (CP /sessions/:id/events) which
    // packs MA fields under `data: {...}`. The reducer + renderers read
    // `createdAt` off the top of SessionEventOnWire, so lift it here so
    // seed events match the same wire shape as the normalized live
    // stream. Without this, the time chips render only for live frames.
    // biome (noParameterAssign): use a local var rather than
    // mutating the action parameter, then read from it below.
    const normalizedSeed = action.events.map((ev) => {
      if (ev.createdAt !== undefined) return ev;
      const dataObj = ev.data as { created_at?: unknown; processed_at?: unknown };
      const ts =
        typeof dataObj?.created_at === 'string' && dataObj.created_at.length > 0
          ? dataObj.created_at
          : typeof dataObj?.processed_at === 'string' && dataObj.processed_at.length > 0
            ? dataObj.processed_at
            : undefined;
      return ts !== undefined ? { ...ev, createdAt: ts } : ev;
    });
    // History replay from `runs.events`. The seed is ALWAYS older than
    // any live SSE frames we may already have received — the server-
    // side semantics guarantee that the GET /sessions/:id/events
    // endpoint returns events up to the moment the request was served,
    // and the live SSE feed delivers events from that moment forward.
    //
    // **Race + ordering guarantee** (codex impl-review round 2 follow-
    // up): prior to this fix the reducer folded seed events INTO the
    // current state via the per-event append path. That preserved the
    // live frames (good) but appended history AFTER them in the
    // projection (bad — the rendered chat showed `[live, hist1, hist2]`
    // when the SSE handshake won the race). The fix: rebuild the
    // event log as `[...history, ...alreadyReceivedLive]` (history
    // first, dedup by id), then re-fold through the per-event reducer
    // from a fresh initial state. The output projection always reads
    // in true chronological order regardless of arrival order.
    //
    // The per-event reducer's id-dedup defends the second branch:
    // a re-seed (or a re-emitted history event the live channel
    // duplicates on reconnect) collapses to a single row in the log.
    const seenIds = new Set<string>();
    const orderedEvents: SessionEventOnWire[] = [];
    for (const ev of normalizedSeed) {
      if (seenIds.has(ev.id)) continue;
      seenIds.add(ev.id);
      orderedEvents.push(ev);
    }
    for (const ev of state.events) {
      if (seenIds.has(ev.id)) continue;
      seenIds.add(ev.id);
      orderedEvents.push(ev);
    }
    // Re-fold from a fresh base. We preserve the live-state
    // connection flag so an `open`/`connecting` transition that
    // already landed survives the seed merge.
    const base: StreamState = { ...initialState(), connection: state.connection };
    let next = base;
    for (const ev of orderedEvents) {
      next = reduceStream(next, { type: 'event', event: ev, lastEventId: ev.id });
    }
    return next;
  }

  const ev = action.event;
  // De-dup by id (the BFF re-fan can replay on reconnect with
  // `Last-Event-ID`; the same event may arrive again).
  if (state.events.some((e) => e.id === ev.id)) {
    return { ...state, lastEventId: action.lastEventId };
  }

  let { messages, status, pendingApproval, lastError, tokens, expectBootstrapAck } = state;

  switch (ev.type) {
    case 'user.message': {
      const content = extractText(ev.data);
      const isBootstrap = isBootstrapContent(content);
      messages = [
        ...messages,
        { kind: 'user', id: ev.id, content, ...(isBootstrap ? { isBootstrap: true } : {}) },
      ];
      // A bootstrap user.message arms the ack-tag for the next
      // agent.message; a real user.message disarms it (the bootstrap
      // never got an ack and we move on).
      expectBootstrapAck = isBootstrap;
      break;
    }
    case 'agent.message': {
      const usage = (ev.data as { usage?: unknown }).usage;
      const usageOut: { input?: number; output?: number } = {};
      if (usage !== null && typeof usage === 'object') {
        const u = usage as { input_tokens?: unknown; output_tokens?: unknown };
        if (typeof u.input_tokens === 'number') usageOut.input = u.input_tokens;
        if (typeof u.output_tokens === 'number') usageOut.output = u.output_tokens;
      }
      const isAck = expectBootstrapAck;
      const thinking = extractThinking(ev.data);
      messages = [
        ...messages,
        {
          kind: 'assistant',
          id: ev.id,
          content: extractText(ev.data),
          ...(usageOut.input !== undefined || usageOut.output !== undefined
            ? { usage: usageOut }
            : {}),
          ...(isAck ? { isBootstrapAck: true } : {}),
          ...(thinking.length > 0 ? { thinking } : {}),
        },
      ];
      expectBootstrapAck = false;
      break;
    }
    case 'agent.custom_tool_use':
    case 'agent.tool_use': {
      const p = ev.data as {
        tool_use_id?: unknown;
        custom_tool_use_id?: unknown;
        name?: unknown;
        input?: unknown;
      };
      // **Anthropic-direct doesn't include `tool_use_id` on the event
      // payload — it surfaces it implicitly via the matching
      // `agent.tool_result` event id linkage.** The platform's
      // webhook handler (handle-idled.ts) falls back to `event.id`
      // for the same reason; mirror that here so the message
      // renders. Pre-fix, this guard required tool_use_id and
      // silently dropped every bash/python tool call.
      if (typeof p.name === 'string') {
        // fn-2.x — `agent.custom_tool_use` events carry the correlator
        // under `custom_tool_use_id` instead of `tool_use_id`. Both
        // fall back to the event id (the platform's webhook handler
        // does the same: see `handle-idled.ts`).
        const toolUseId =
          typeof p.tool_use_id === 'string' && p.tool_use_id.length > 0
            ? p.tool_use_id
            : typeof p.custom_tool_use_id === 'string' && p.custom_tool_use_id.length > 0
              ? p.custom_tool_use_id
              : ev.id;
        messages = [
          ...messages,
          {
            kind: 'tool_use',
            id: ev.id,
            toolUseId,
            toolName: p.name,
            input:
              p.input !== undefined &&
              p.input !== null &&
              typeof p.input === 'object' &&
              !Array.isArray(p.input)
                ? (p.input as Record<string, unknown>)
                : {},
          },
        ];
      }
      break;
    }
    case 'agent.tool_result': {
      const p = ev.data as { tool_use_id?: unknown; content?: unknown; is_error?: unknown };
      if (typeof p.tool_use_id === 'string') {
        let output = '';
        if (typeof p.content === 'string') {
          output = p.content;
        } else if (Array.isArray(p.content)) {
          for (const block of p.content) {
            if (
              block !== null &&
              typeof block === 'object' &&
              (block as { type?: unknown }).type === 'text' &&
              typeof (block as { text?: unknown }).text === 'string'
            ) {
              output += (block as { text: string }).text;
            }
          }
        }
        // fn-2.x — bundle into the matching tool_use row when present
        // rather than pushing a sibling row. Keeps the transcript
        // visually compact (one card per tool call). Fall back to a
        // standalone tool_result row when the originating tool_use is
        // missing (replay race, partial history).
        const idx = messages.findIndex(
          (m) => m.kind === 'tool_use' && m.toolUseId === p.tool_use_id,
        );
        if (idx >= 0) {
          const tu = messages[idx] as Extract<ChatMessage, { kind: 'tool_use' }>;
          const updated: ChatMessage = {
            ...tu,
            result: {
              id: ev.id,
              output,
              ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
            },
          };
          messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
        } else {
          const next: ChatMessage = {
            kind: 'tool_result',
            id: ev.id,
            toolUseId: p.tool_use_id,
            output,
            ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
          };
          messages = [...messages, next];
        }
      }
      break;
    }
    case 'user.custom_tool_result': {
      // fn-2.x — operator's elicitation answer (e.g. submitted via
      // the AskUserQuestion wizard). MA emits this AFTER the
      // matching `agent.custom_tool_use` requires_action gate is
      // resolved via `approvals.decide`. The payload's
      // `data.content[].text` is the JSON-stringified
      // `{questions, answers}` form-data; we bundle it into the
      // tool_use row's `result` so the transcript renders "Q → A"
      // pairs inline.
      const p = ev.data as {
        custom_tool_use_id?: unknown;
        content?: unknown;
        is_error?: unknown;
      };
      if (typeof p.custom_tool_use_id !== 'string') break;
      let output = '';
      if (typeof p.content === 'string') {
        output = p.content;
      } else if (Array.isArray(p.content)) {
        for (const block of p.content) {
          if (
            block !== null &&
            typeof block === 'object' &&
            (block as { type?: unknown }).type === 'text' &&
            typeof (block as { text?: unknown }).text === 'string'
          ) {
            output += (block as { text: string }).text;
          }
        }
      }
      const idx = messages.findIndex(
        (m) => m.kind === 'tool_use' && m.toolUseId === p.custom_tool_use_id,
      );
      if (idx >= 0) {
        const tu = messages[idx] as Extract<ChatMessage, { kind: 'tool_use' }>;
        const updated: ChatMessage = {
          ...tu,
          result: {
            id: ev.id,
            output,
            ...(typeof p.is_error === 'boolean' ? { isError: p.is_error } : {}),
          },
        };
        messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
      }
      break;
    }
    case 'user.tool_confirmation': {
      const p = ev.data as {
        tool_use_id?: unknown;
        result?: unknown;
        deny_message?: unknown;
      };
      if (typeof p.tool_use_id === 'string' && (p.result === 'allow' || p.result === 'deny')) {
        // Correlate back to the originating agent.tool_use event so we
        // can pick up the smokeball annotation (decision_actor_user_id)
        // from `GET /events`. The live SSE stream is raw MA and does
        // not carry the annotation; the first paint (seedEvents) does.
        const originating = state.events.find(
          (e) =>
            e.type === 'agent.tool_use' &&
            (e.id === p.tool_use_id ||
              (e.data as { tool_use_id?: unknown }).tool_use_id === p.tool_use_id),
        );
        const decidedBy =
          originating !== undefined &&
          originating.smokeball !== undefined &&
          typeof (originating.smokeball as { decision_actor_user_id?: unknown })
            .decision_actor_user_id === 'string'
            ? (originating.smokeball as { decision_actor_user_id: string }).decision_actor_user_id
            : undefined;
        const denyMessage =
          typeof p.deny_message === 'string' && p.deny_message.length > 0
            ? p.deny_message
            : undefined;
        // Annotate the matching tool_use row instead of pushing a new
        // sibling row. The MA event log emits user.tool_confirmation
        // at the moment we POST the resume — typically AFTER the
        // matching agent.tool_result has already landed. Rendering it
        // as its own row puts the decision visually after the result,
        // which is confusing. Inline it on the tool row instead.
        const idx = messages.findIndex(
          (m) => m.kind === 'tool_use' && m.toolUseId === p.tool_use_id,
        );
        if (idx >= 0) {
          const tu = messages[idx] as Extract<ChatMessage, { kind: 'tool_use' }>;
          const updated: ChatMessage = {
            ...tu,
            decision: p.result,
            ...(ev.createdAt !== undefined ? { decidedAt: ev.createdAt } : {}),
            ...(decidedBy !== undefined ? { decidedBy } : {}),
            ...(denyMessage !== undefined ? { denyMessage } : {}),
          };
          messages = [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)];
        } else {
          // fn-2.x (CodeRabbit) — preserve the decision even when the
          // originating `agent.tool_use` row never landed (partial
          // history replay, malformed payload, future wire drift).
          // Otherwise the approval would silently disappear.
          messages = [
            ...messages,
            {
              kind: 'tool_confirmation',
              id: ev.id,
              toolUseId: p.tool_use_id,
              decision: p.result,
              ...(ev.createdAt !== undefined ? { decidedAt: ev.createdAt } : {}),
              ...(decidedBy !== undefined ? { decidedBy } : {}),
              ...(denyMessage !== undefined ? { denyMessage } : {}),
            },
          ];
        }
        // Clear pending if this confirmation matches the gate.
        if (pendingApproval !== null && pendingApproval.toolUseId === p.tool_use_id) {
          pendingApproval = null;
        }
      }
      break;
    }
    case 'session.status_idle': {
      status = 'idle';
      const sr = (ev.data as { stop_reason?: unknown }).stop_reason;
      // Need to include the just-arrived `agent.tool_use` rows in the
      // lookup, since the idle frame typically lands right after.
      pendingApproval = derivePending([...state.events, ev], sr);
      break;
    }
    case 'session.status_running': {
      status = 'running';
      pendingApproval = null;
      break;
    }
    case 'session.status_terminated': {
      status = 'terminated';
      pendingApproval = null;
      break;
    }
    case 'span.model_request_end': {
      // MA records per-turn LLM usage on this event. Aggregate it into
      // the live token counter on the StreamState so the session
      // header chip + future per-turn pills can render without a
      // durable column on `sessions`. Field names per 2026-05-16 wire
      // dump: `model_usage.{input_tokens, output_tokens,
      // cache_read_input_tokens, cache_creation_input_tokens}`.
      const usage = (ev.data as { model_usage?: unknown }).model_usage;
      if (usage !== null && typeof usage === 'object') {
        const u = usage as {
          input_tokens?: unknown;
          output_tokens?: unknown;
          cache_read_input_tokens?: unknown;
          cache_creation_input_tokens?: unknown;
        };
        tokens = {
          input: tokens.input + (typeof u.input_tokens === 'number' ? u.input_tokens : 0),
          output: tokens.output + (typeof u.output_tokens === 'number' ? u.output_tokens : 0),
          cacheRead:
            tokens.cacheRead +
            (typeof u.cache_read_input_tokens === 'number' ? u.cache_read_input_tokens : 0),
          cacheCreate:
            tokens.cacheCreate +
            (typeof u.cache_creation_input_tokens === 'number' ? u.cache_creation_input_tokens : 0),
        };
      }
      break;
    }
    case 'session.error': {
      const p = ev.data as { type?: unknown; message?: unknown };
      lastError = {
        type: typeof p.type === 'string' ? p.type : 'unknown',
        message: typeof p.message === 'string' ? p.message : '',
      };
      break;
    }
    default:
      // Unknown / unhandled event types are kept in the log but don't
      // change the projection. This keeps the reducer forward-
      // compatible with new MA event kinds.
      break;
  }

  return {
    ...state,
    messages,
    status,
    pendingApproval,
    lastError,
    tokens,
    expectBootstrapAck,
    events: [...state.events, ev],
    lastEventId: action.lastEventId,
  };
}

/**
 * Inputs to `useSessionStream`. The hook owns the EventSource
 * lifecycle; the caller passes the session id + optional seed events.
 */
export interface UseSessionStreamInputs {
  /** Session id to stream. `null` disables the connection. */
  readonly sessionId: string | null;
  /**
   * Optional historical events (from `runs.events`) to seed the
   * reducer before live frames arrive. Replaying through the same
   * reducer keeps the projection identical regardless of source.
   *
   * **When to seed**: on the run-detail page's initial render,
   * `runs.events` is fetched first; pass `data.data` here so the
   * page shows prior turns instantly. The hook de-dupes by id, so a
   * subsequent live replay of the same ids is a no-op.
   */
  readonly seedEvents?: readonly SessionEventOnWire[];
  /**
   * Optional connection factory (tests inject a fake). Defaults to
   * `connectSessionStream` from `session-stream.ts`.
   */
  readonly connectFn?: typeof connectSessionStream;
  /**
   * Optional URL builder (tests inject a custom path). Defaults to
   * `buildSessionStreamUrl`.
   */
  readonly urlBuilder?: typeof buildSessionStreamUrl;
}

/**
 * Subscribe to a session stream and surface a `StreamState`.
 *
 * **Lifecycle**:
 *   1. On mount (or when sessionId changes): reset state, open the
 *      stream via `connectSessionStream`.
 *   2. While open: dispatch frames into the reducer.
 *   3. On unmount: `handle.close()`.
 *
 * **Why `useSyncExternalStore` is NOT used here**: the underlying
 * stream is not a React-friendly snapshot source — frames arrive
 * asynchronously and produce a derived state via a reducer. A
 * `useReducer` + `useEffect` pair is the canonical shape for this; we
 * keep imports clean (the import of `useSyncExternalStore` was a
 * legacy from the v0 doc-comment — we removed it).
 */
export function useSessionStream(inputs: UseSessionStreamInputs): StreamState {
  const [state, dispatch] = useReducer(reduceStream, undefined, initialState);

  // **Session-id transition reset** (codex impl-review round 2 fix).
  // The previous shape dispatched `reset` inside the connect effect,
  // which fires on every `url` change. The chain was:
  //   1. Mount with sessionId=A — connect effect dispatches reset
  //      (state is already initial, fine).
  //   2. Seed arrives — dispatch seed, state populates.
  //   3. `connectFn` ref changes (test rebuild / hot reload) → connect
  //      effect re-runs → dispatches reset → seed is gone, the next
  //      run of the seed effect (seed ref unchanged) never re-fires.
  // The fix: reset is scoped to the SESSION-ID transition, not the
  // url/connectFn deps. We compare the current sessionId to the
  // ref-cached previous one and dispatch reset only on a genuine
  // session change. New sessions still see a clean state; same-
  // session re-connects preserve the seed.
  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current !== inputs.sessionId) {
    sessionIdRef.current = inputs.sessionId;
    // Layout-effect-style reset: dispatch synchronously during render so
    // the post-reset state is what downstream effects observe. This is
    // safe — `useReducer` allows dispatches during render so long as the
    // call is gated on a value change (React eats no-op re-renders).
    dispatch({ type: 'reset' });
  }

  // Seed effect: fold history into current state (idempotent via
  // per-event id dedup). Runs whenever `seedEvents` reference changes.
  const seed = inputs.seedEvents ?? null;
  useEffect(() => {
    if (seed === null || seed.length === 0) return;
    dispatch({ type: 'seed', events: seed });
  }, [seed]);

  const connectFn = inputs.connectFn ?? connectSessionStream;
  const urlBuilder = inputs.urlBuilder ?? buildSessionStreamUrl;

  // Memoize the SSE URL so the effect doesn't reopen on every render.
  const url = useMemo(() => {
    if (inputs.sessionId === null || inputs.sessionId.length === 0) return null;
    try {
      return urlBuilder(inputs.sessionId);
    } catch {
      return null;
    }
  }, [inputs.sessionId, urlBuilder]);

  useEffect(() => {
    if (url === null) return;
    dispatch({ type: 'connection', state: 'connecting' });
    // NB: do NOT dispatch reset here. Reset is scoped to sessionId
    // transitions (above) so seeds + already-received live frames
    // survive a connectFn reference change.
    let handle: SessionStreamHandle | null = null;
    try {
      handle = connectFn({ url });
    } catch {
      // Connection error at construction time — surface via state by
      // immediately transitioning to closed.
      dispatch({ type: 'connection', state: 'closed' });
      return;
    }
    const offMessage = handle.onMessage((frame: SessionStreamFrame) => {
      // Parse-failure frames have `data === null` (the wrapper logs +
      // fans out as error). Skip the dispatch.
      if (frame.data === null) return;
      // **Two upstream wire shapes** the BFF re-fans transparently:
      //   1. **Nested** (`{id, type, data: {...rest}, smokeball?}`) —
      //      the CP `GET /sessions/:id/events` enrichment route. Seed
      //      events go through this. Used on first paint + on refresh.
      //   2. **Flat MA envelope** (`{id, type, created_at, content,
      //      stop_reason, ...}`) — the live SSE path stringifies the
      //      raw MA event verbatim (`replay-from-ma.ts:333`), no
      //      repacking. So `d.data` is undefined and every event
      //      payload sat at the top level.
      // Normalize here so the reducer sees one shape: copy everything
      // that isn't `id`/`type`/`smokeball` into a synthetic `data`
      // bag when nested form is absent.
      if (frame.data === null || typeof frame.data !== 'object') return;
      const d = frame.data as Record<string, unknown>;
      if (typeof d.id !== 'string' || typeof d.type !== 'string') return;
      const nestedData =
        d.data !== undefined &&
        d.data !== null &&
        typeof d.data === 'object' &&
        !Array.isArray(d.data)
          ? (d.data as Record<string, unknown>)
          : null;
      const data: Record<string, unknown> =
        nestedData !== null
          ? nestedData
          : (() => {
              const flat: Record<string, unknown> = {};
              for (const [k, v] of Object.entries(d)) {
                if (k === 'id' || k === 'type' || k === 'smokeball') continue;
                flat[k] = v;
              }
              return flat;
            })();
      // Pull the timestamp from any of three places, in order of
      // preference: top-level `created_at` (live MA envelope when
      // present), nested `data.created_at` (enriched /events when
      // present), and finally `data.processed_at` (the field MA
      // actually emits on every event today — observed 2026-05-16).
      const tryStr = (v: unknown): string | undefined =>
        typeof v === 'string' && v.length > 0 ? v : undefined;
      const createdAtRaw =
        tryStr(d.created_at) ??
        tryStr((data as { created_at?: unknown }).created_at) ??
        tryStr((data as { processed_at?: unknown }).processed_at) ??
        tryStr(d.processed_at);
      const event: SessionEventOnWire = {
        id: d.id,
        type: d.type,
        data,
        ...(d.smokeball !== null && typeof d.smokeball === 'object' && !Array.isArray(d.smokeball)
          ? { smokeball: d.smokeball as Record<string, unknown> }
          : {}),
        ...(createdAtRaw !== undefined ? { createdAt: createdAtRaw } : {}),
      };
      dispatch({ type: 'event', event, lastEventId: frame.lastEventId });
    });
    const offOpen = handle.onOpen(() => {
      dispatch({ type: 'connection', state: 'open' });
    });
    const offError = handle.onError(() => {
      // EventSource auto-reconnects on error. We don't tear down on
      // a single error — the connection state stays "open" until
      // close() runs. We DO flip to "connecting" so the UI can
      // surface a transient indicator; a subsequent open() flips
      // back. (Useful for "Reconnecting..." indicator.)
      dispatch({ type: 'connection', state: 'connecting' });
    });
    return (): void => {
      offMessage();
      offOpen();
      offError();
      handle?.close();
      dispatch({ type: 'connection', state: 'closed' });
    };
  }, [url, connectFn]);

  return state;
}

/**
 * Trivial helper — type alias for `useSyncExternalStore` snapshot
 * pattern, exported for tests that want to drive the reducer directly
 * without mounting a React tree.
 *
 * **Why this isn't part of `StreamState` itself**: keeping the
 * reducer pure (a function on state + action) lets tests verify
 * individual reductions without a full hook harness. Tests that DO
 * mount the hook use `renderHook` semantics with a fake `connectFn`.
 */
export { initialState as _initialStreamStateForTests };

// Imported by tests + the run-detail page; re-export for convenience.
export type { SessionStreamFrame, SessionStreamHandle } from './session-stream.js';
